import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { EntityData } from '@ContractBuilder/types';
import type { Step } from '@features/create-entity-modal/types';
import { zodResolver } from '@hookform/resolvers/zod';

import type { CreateContractFormState } from '../types';
import { getValidationSchema } from '../utils/getValidationSchema';

export const useCreateContractForm = (currentStep: Step, contract?: EntityData) => {
  const validationSchema = useMemo(() => getValidationSchema(currentStep), [currentStep]);

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setError,
    setValue,
    trigger,
    watch,
  } = useForm<CreateContractFormState>({
    defaultValues: { team: [] },
    context: { currentStep },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (contract) {
      setValue('team', contract.teams ?? []);
      setValue('name', contract.name);
      trigger();
    }
  }, [contract, setValue, trigger]);

  useEffect(() => {
    trigger();
    // eslint-disable-next-line -- We want to re-trigger the validation resolver when navigating between steps to avoid stale cached data
  }, [currentStep]);

  const formValues = watch();

  return {
    control,
    formValues,
    handleSubmit,
    isValid,
    setError,
    setValue,
  };
};
